import { ActivitySpinnerWidget, ButtonType, ButtonWidget, DropdownButtonType, List, ListItem } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';
import _ from 'underscore';

import { FormikSingleSelectDropdown } from '~src/commonInfra/components/FormikSingleSelectDropdown';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ListDialogErrorAlert } from '~src/connectivity/components/AddCredentialAuthModeDialog';
import { IValidateCustomOAuthConfigResult, ValidateCustomOAuthConfigRequest } from '~src/connectivity/serverApi/requests/OAuth/ValidateCustomOAuthConfigRequest';
import { IOAuthCredentialTypeInfo, OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { OAuthCredentialsService } from '~src/connectivity/services/OAuthCredentialsService';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { AddOAuthClientDialogStyles } from '~src/settings/general-settings/styles/oauth-client/AddOAuthClientDialogStyles';
import { UserSettingsStyle } from '~src/userSettings/styles/UserSettingsStyle';

import { Messages } from '../../../../../codegen/Localize';
import { FormikTextBox } from '../../../../../commonInfra/components/FormikField';
import { textFieldWidget } from '../../../../../userSettings/styles/EmailAndDisplayNameFormStyles';

export interface OAuthClientSetting {
  dbClass: string;
  instanceUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthClientRedirectUri: string;
  oauthConfigId?: string;
  configLabel?: string;
  oauthConfigContent?: string;
  fileName?: string;
  id: string;
}

export type OAuthClientsFormField = { oauthClients: OAuthClientSetting[] }
export type OAuthClientsFormikProps = FormikProps<OAuthClientsFormField>;

export interface AddOAuthClientDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  addOAuthClientSubmit: (values: AddOAuthClientDialogState) => void;
  hardcodeConnectionType?: string;
  showInstanceUrlSection: boolean;
}

export interface AddOAuthClientDialogState {
  dbClass: string;
  instanceUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthClientRedirectUri: string;
  oauthConfigId?: string;
  configLabel?: string;
  oauthConfigContent?: string;
  customOAuthConfigId?: string;
  fileName?: string;
  hasFileErrors?: boolean;
  validatingFile?: boolean;
}

// revise the regex pattern from https://www.tutorialspoint.com/How-to-validate-URL-address-in-JavaScript
const urlPattern = new RegExp('^(https?:\\/\\/)' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{1,}|localhost|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
  '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
  '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator

// redirectUrl matching pattern.
const redirectUrlPattern = new RegExp('(?=^.{1,256}$)' + //length limit
  '(^(https?:\\/\\/)' + // protocol
  '(?:(?!\\.|-)([a-zA-Z0-9_\\-]\\.?)+)' + // domain name(Matches any FQDNs or non-FQDNs except those starting with '.' or '-', in accordance with the oauth spec guidelines for redirect_url registration)
  '(\\:\\d+)?' + // port
  '(\\/auth\\/add_oauth_token))');// redirectUrl path has to be /auth/add_oauth_token

const embeddedWhiteSpace = /\s/;

const ACCEPTED_FILE_TYPES = ['.xml'];

const FILE_SIZE_LIMIT = 2 * 1024 * 1024;

let oauthConfigIdToLabel: {[key: string]: string} = {};

function validateUrl(value: string): boolean {
  return !!urlPattern.test(value);
}

function validateRedirectUrl(value: string): boolean {
  if (value.startsWith('http://')) {
    // revised from http://scratch99.com/web-development/javascript/how-to-get-the-domain-from-a-url/
    let domain = value.replace('http://', '').split(/[/?#:]/)[0];
    if (domain !== 'localhost') {
      return false;
    }
  }
  return !!redirectUrlPattern.test(value);
}

export function validateNoEmbeddedWhiteSpaceCI(value: string): string {
  if (!value || embeddedWhiteSpace.test(value)) {
    return Messages.invalidCI();
  }
  return '';
}

export function validateNoEmbeddedWhiteSpaceCS(value: string): string {
  if (embeddedWhiteSpace.test(value)) {
    return Messages.invalidCS();
  }
  return '';
}

export function validateInstanceUrl(value: string): string {
  let v: string;
  if (value && (v = value.trim()) && !validateUrl(v)) {
    return Messages.invalidUrl();
  }
  return '';
}

export function validateRedirectUri(value: string): string {
  if (!value || !validateRedirectUrl(value.trim())) {
    return Messages.invalidRedirectUrl();
  }
  return '';
}

export async function validateCustomOAuthConfig(stringContent: string, dbclass: string): Promise<IValidateCustomOAuthConfigResult> {
  return WebHttpClient.sendRequest(new ValidateCustomOAuthConfigRequest({ customOAuthConfig : stringContent, dbClass : dbclass }));
}

    /**
   * Helper method that Promises the file content of the uploaded file.
   * 
   * @param fileInput XML File object uploaded by user.
   * @returns A promise for the string content of the uploaded file
   */
    export const readFileContent = async (fileInput: File): Promise<string> => {
      let content = '';

      return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onerror = () => {
          reader.abort();
          reject(new DOMException('Could not read file'));
        };
        reader.onloadend = (event) => {
          content = event.target.result as string;
          resolve(content);
        };
        reader.readAsText(fileInput, 'UTF-8');
      });
    };

export class AddOAuthClientDialog extends React.Component<AddOAuthClientDialogProps, AddOAuthClientDialogState> {
  private customIDP = Messages.customIDP();

  fileInput: React.RefObject<HTMLInputElement>;

  constructor(props: AddOAuthClientDialogProps) {
    super(props);
    this.fileInput = React.createRef();
  }

  async handleSubmit(client: OAuthClientSetting, formik: FormikHelpers<OAuthClientSetting>) {
    formik.setSubmitting(true);
    // If Multiple IDP and BYO Idp are enabled, Custom IDP is picked, and custom OAuth Config ID 
    // is parsed, update the OAuth Config ID and OAuth Config Content.  Otherwise, just submit current client object.
    if (FeatureFlags.oauthMultipleIdpOnServer && FeatureFlags.oauthBYOIdp && client.oauthConfigId === this.customIDP && this.state?.customOAuthConfigId) {
      // Store uploaded file name in localStorage.
      window.localStorage.setItem([client.dbClass, client.oauthClientId, this.state?.customOAuthConfigId].join(':'), this.state?.fileName);

      // Update instanceUrl, oauthConfigId, configLabel, oauthConfigContent, and fileName
      client.instanceUrl = '';
      client.oauthConfigId = this.state?.customOAuthConfigId;
      client.configLabel = this.state?.configLabel || this.state?.customOAuthConfigId;
      client.oauthConfigContent = this.state?.oauthConfigContent;
      client.fileName = this.state?.fileName;
    }
    if (oauthConfigIdToLabel[client.oauthConfigId]) {
      // Add configLabel to client for case where oauthConfigId is present
      // Every client with an oauthConfigId will also have a configLabel
      client.configLabel = oauthConfigIdToLabel[client.oauthConfigId];
    }

    this.props.addOAuthClientSubmit(client);
    formik.setSubmitting(false);
    this.props.close(true);
  }

  private getConnectionTypeDropdownSection(formik: FormikProps<AddOAuthClientDialogState>, oauthCredentialTypes: IOAuthCredentialTypeInfo[]): JSX.Element {
    const credentialTypesList = oauthCredentialTypes
    .map<ListItem>(typeInfo => { return { value: OAuthCredentialsService.getDisplayNameFromInfo(typeInfo) }; });
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='dbClass'>{Messages.oauthClientDbClass()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikSingleSelectDropdown
            name='dbClass'
            list={<List items={credentialTypesList} className={UserSettingsStyle.dropdownList} />}
            type={DropdownButtonType.Outline}
            label={''}
            className={AddOAuthClientDialogStyles.dropdownStyle}
            testId={'add-oauth-client-choose-existing-dbclass'}
            value={formik.values.dbClass}
            valid={formik.values.dbClass !== Messages.addOAuthClient_chooseExisting()}
            disabled={!_.isUndefined(this.props.hardcodeConnectionType) || formik.isSubmitting}
          />
        </td>
      </tr>
    );
  }

  private getOAuthConfigIdSection(formik: FormikProps<AddOAuthClientDialogState>, oauthCredentialTypes: IOAuthCredentialTypeInfo[]): JSX.Element {
    const oauthCredentialType = oauthCredentialTypes
    .find(typeInfo => OAuthCredentialsService.getDisplayNameFromInfo(typeInfo) === formik.values.dbClass || typeInfo.type === formik.values.dbClass);
    let oauthConfigList: ListItem[] = [];
    if(oauthCredentialType) {
        oauthConfigList = oauthCredentialType.oauthConfigInfoList
        .map<ListItem>(oauthConfigInfo => {
          // Store oauthConfigId => configLabel mapping for when submitting
          oauthConfigIdToLabel[oauthConfigInfo.oauthConfigId] = oauthConfigInfo.configLabel;
          return {value: oauthConfigInfo.oauthConfigId, content: oauthConfigInfo.configLabel};
        });
    }

    // Add 'Custom IDP' option to oauth configs drop down list
    if (FeatureFlags.oauthMultipleIdpOnServer && FeatureFlags.oauthBYOIdp && oauthConfigList.length >= 1 && oauthConfigList[0].value !== '') {
      let newList = oauthConfigList.concat([{value: this.customIDP}]);
      oauthConfigList = newList;
      // Remove default idp option for connectors that don't support any default idp.
      if (OAuthCredentialsService.shouldIgnoreDefaultOAuthConfigId(oauthCredentialType.type)){
        oauthConfigList.shift();
      }
    }

    // Clear the oauthConfigId section if current dbClass does not support that
    if(oauthConfigList.length === 0) {
      formik.values.oauthConfigId = '';
    }
    // Default to the first oauthConfigId 
    else if(!oauthConfigList.find(oauthConfig => oauthConfig.value === formik.values.oauthConfigId)) {
      formik.values.oauthConfigId = oauthConfigList[0].value;
    }

    return (oauthConfigList.length >= 1 && oauthConfigList[0].value !== '' &&
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='oauthConfigId'>{Messages.oauthClientProvider()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikSingleSelectDropdown
            name='oauthConfigId'
            list={<List items={oauthConfigList} className={UserSettingsStyle.dropdownList} />}
            type={DropdownButtonType.Outline}
            label={''}
            className={AddOAuthClientDialogStyles.dropdownStyle}
            testId={'add-oauth-client-choose-oauth-type'}
            value={formik.values.oauthConfigId}
            disabled={!oauthConfigList.length || formik.isSubmitting}
          />
        </td>
      </tr>
    );
  }

  private getCustomOAuthConfigIdSection(formik: FormikProps<AddOAuthClientDialogState>): JSX.Element {
    return (formik.values.oauthConfigId === this.customIDP &&
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='customOAuthConfigId'>{Messages.customOAuthConfigId()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
        <FormikTextBox
            name='customOAuthConfigId'
            testId='customOAuthConfigId'
            label=''
            className={textFieldWidget}
            value={(!this.state?.fileName || this.state?.hasFileErrors || this.state?.dbClass !== formik.values?.dbClass) ? Messages.customOAuthConfigIdUploadMsg() : (this.state?.configLabel ||  this.state?.customOAuthConfigId)}
            contentEditable={false}
            disabled={true}
          />
        </td>
      </tr>
    );
  }

  private getInstanceUrlSection(): JSX.Element {
    if (!this.props.showInstanceUrlSection) return <></>;
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='instanceUrl'>{Messages.oauthClientInstanceUrl()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='instanceUrl'
            testId='instanceUrl'
            label=''
            validate={validateInstanceUrl}
            className={textFieldWidget}
            showValidationMessage={true}
            placeholder='Optional'
          />
        </td>
      </tr>
    );
  }

  private getClientIdSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='oauthClientId'>{Messages.oauthClientId()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='oauthClientId'
            testId='oauthClientId'
            label=''
            validate={validateNoEmbeddedWhiteSpaceCI}
            className={textFieldWidget}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  }

  private getClientSecretSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='oauthClientSecret'>{Messages.oauthClientSecret()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='oauthClientSecret'
            testId='oauthClientSecret'
            label=''
            validate={validateNoEmbeddedWhiteSpaceCS}
            className={textFieldWidget}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  }

  private getRedirectUrlSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='oauthClientRedirectUri'>{Messages.oauthRedirectUri()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide' title={Messages.oauthRedirectUri_input_tooltip()}>
          <FormikTextBox
            name='oauthClientRedirectUri'
            testId='oauthClientRedirectUri'
            label=''
            validate={validateRedirectUri}
            className={textFieldWidget}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  }

  private getOAuthConfigContentSection(formik: FormikProps<AddOAuthClientDialogState>): JSX.Element {
    let fileStatus =  (this.state?.fileName ?
                        (this.state?.hasFileErrors || this.state?.dbClass !== formik.values?.dbClass ?
                          AddOAuthClientDialogStyles.invalidFileColor :
                          AddOAuthClientDialogStyles.validFileColor):
                        AddOAuthClientDialogStyles.defaultFileColor);
    return(formik.values.oauthConfigId === this.customIDP &&
      <tr data-tb-test-id="add-oauth-client-file-upload">
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='oauthConfigContent'>{Messages.oauthClientConfigContent()}</label>
        </td>
        <td className={AddOAuthClientDialogStyles.fileSelectionContainer + ' ' + fileStatus}>
          <input
            data-tb-test-id="add-oauth-config-file-input"
            type="file"
            name="file"
            ref={this.fileInput}
            style={{display: 'none'}}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleFileSelection(event, formik)}
            onClick={this.resetUploadedFile}
            accept={ACCEPTED_FILE_TYPES.join(',')}
            />
          <div className={AddOAuthClientDialogStyles.dragAndDropChildren}>
              {!this.state?.validatingFile && this.state?.fileName &&
                  <b className={AddOAuthClientDialogStyles.textMargin + ' ' + fileStatus}>
                    {this.state?.fileName}
                  </b>
              }
              {this.state?.validatingFile &&
                <b className={AddOAuthClientDialogStyles.spinnerStyle}>
                  <ActivitySpinnerWidget testId={'file-upload-spinner'} shouldShowUnderlay={false} />
                </b>
              }
              <ButtonWidget
                  buttonType={ButtonType.Outline}
                  type="button"
                  testId="upload-xml-file"
                  children={Messages.contentUpload_chooseAFile()}
                  handleClick={this.triggerFileInput}
                  disabled={formik.isSubmitting}
              />
          </div>
        </td>
      </tr>
    );
  }

  private getErrorMessageBanner(formik: FormikProps<AddOAuthClientDialogState>): JSX.Element {
    return(formik.values.oauthConfigId === this.customIDP && this.state?.fileName && (this.state?.hasFileErrors || this.state?.dbClass !== formik.values?.dbClass) &&
        <tr data-tb-test-id="add-oauth-client-error-banner">
          <td className='tb-text-input-label-cell tb-wide-overflow'>
            <label htmlFor='errorMessageBanner'>{}</label>
          </td>
          <td data-tb-test-id="file-upload-error-message">
            <ListDialogErrorAlert
                testId={'invalid-custom-config-alert'}
                message={Messages.invalidOAuthConfigFile()}
                errorListTitle={Messages.addNewSavedCredentialServiceAccountInvalidFile_WhatToTryNext()}
                errors={[Messages.validateCustomOAuthConfigFailure_LogDetails(), Messages.validateCustomOAuthConfigFailure_SdkDocs()]}
            />
          </td>
        </tr>
    );
  }

  private resetUploadedFile = async (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const element = event.target as HTMLInputElement;
    element.value = '';
  };

  private handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>, formik: FormikProps<AddOAuthClientDialogState>) => {
    // Only set the file if a user has selected a file. They may have hit cancel
    if (event.target.files.length > 0) {
      // Store the uploaded file and file name and update the isFileUploaded flag
      let fileUploaded: File = event.target.files[0];
      this.setState({validatingFile: true});

      let hasFileErrors = false;
      let configId = '';
      let configLabel = '';
      let configContent = '';
      // Set to matched type or set to display name by default
      let dbclass = OAuthCredentialsService.getInfoFromDisplayName(formik.values.dbClass)?.type ?? formik.values.dbClass;

      if (fileUploaded.type !== 'text/xml' || fileUploaded.size > FILE_SIZE_LIMIT) {
        hasFileErrors = true;
      } else {
        let content = await readFileContent(fileUploaded);
        let response = await validateCustomOAuthConfig(content, dbclass);

        if (response.errors) {
          hasFileErrors = true;
        } else {
          configId = response.customOAuthConfigId;
          configLabel = response.configLabel;
          configContent = content;
        }
      }
      this.setState({fileName: fileUploaded.name, oauthConfigContent: configContent, customOAuthConfigId: configId, configLabel: configLabel, dbClass: formik.values?.dbClass, hasFileErrors: hasFileErrors, validatingFile: false});
    }
  };

  private triggerFileInput = () => {
    this.fileInput.current.click();
  };

  public render() {
    const initialValues = {
      dbClass: this.props.hardcodeConnectionType ?
        OAuthCredentialsService.getDisplayNameFromType(this.props.hardcodeConnectionType) :
        Messages.addOAuthClient_chooseExisting(),
      instanceUrl: '',
      oauthClientId: '',
      oauthClientSecret: '',
      oauthClientRedirectUri: '',
      oauthConfigId: '',
      oauthConfigContent: ''
    };
    const allCredentialTypes = OAuthCredentialsService.getAllOAuthCredentialTypes();
    const credentialTypeInfoList: IOAuthCredentialTypeInfo[] =  allCredentialTypes
    .filter(type => !OAuthCredentialsService.supportsGenericAuth(type.type) && type.type !== OAuthCredentialTypes.Slack
      && type.type !== OAuthCredentialTypes.TableauOnlinAuthorizationServer);
    return (
      <Formik
        initialValues={initialValues}
        onSubmit={(values: OAuthClientSetting, formik: FormikHelpers<OAuthClientSetting>) => {
          let dbclass = OAuthCredentialsService.getInfoFromDisplayName(values.dbClass)?.type;
          values.dbClass = !dbclass ? values.dbClass : dbclass;
          this.handleSubmit(values, formik);
        }}
      >
        {(formik: FormikProps<AddOAuthClientDialogState>) => (
          <form onSubmit={formik.handleSubmit} data-tb-test-id='add-oauth-client-dialog'>
            <div className='tb-dialog-title'>{Messages.oauthClientDialogTitle()}</div>
            <table className='tb-form-table'>
              <tbody>
                {this.getConnectionTypeDropdownSection(formik, credentialTypeInfoList)}
                {FeatureFlags.oauthMultipleIdpOnServer && this.getOAuthConfigIdSection(formik, credentialTypeInfoList)}
                {FeatureFlags.oauthMultipleIdpOnServer && FeatureFlags.oauthBYOIdp && this.getCustomOAuthConfigIdSection(formik)}
                {(formik.values.oauthConfigId !== this.customIDP) && this.getInstanceUrlSection()}
                {this.getClientIdSection()}
                {this.getClientSecretSection()}
                {this.getRedirectUrlSection()}
                {FeatureFlags.oauthMultipleIdpOnServer && FeatureFlags.oauthBYOIdp && this.getOAuthConfigContentSection(formik)}
                {FeatureFlags.oauthMultipleIdpOnServer && FeatureFlags.oauthBYOIdp && this.getErrorMessageBanner(formik)}
              </tbody>
            </table>
            <div className='tb-dialog-actions'>
              <ButtonWidget buttonType={ButtonType.Outline}
                testId='add-oauth-client-dialog-dismiss'
                onClick={() => this.props.dismiss()}
                disabled={formik.isSubmitting}
                type='button'>{Messages.dismiss()}
              </ButtonWidget>
              <ButtonWidget buttonType={ButtonType.Primary}
                testId='add-oauth-client-dialog-add-oauth-client-button'
                className='tb-margin-left-wide'
                disabled={!formik.dirty || !formik.isValid || formik.isSubmitting || formik.values.dbClass === Messages.addOAuthClient_chooseExisting() ||
                  (FeatureFlags.oauthMultipleIdpOnServer && FeatureFlags.oauthBYOIdp && formik.values.oauthConfigId === this.customIDP && (!this.state || !this.state.fileName || this.state.hasFileErrors || this.state.dbClass !== formik.values.dbClass))}
                onFocus={() => true}
                type='submit'>{Messages.addOAuthClient_action()}
              </ButtonWidget>
            </div>
          </form>
        )}
      </Formik>
    );
  }
}
